/**
 * Writing a stylesheet onto the elements it styles.
 *
 * Putting a template's `<style>` block in `<head>` is enough for Gmail on the
 * web. It is not enough generally: Gmail clips a message over 102KB and drops
 * the stylesheet with the rest of the head, and several clients discard
 * embedded CSS outright. The only styling every client honours is a `style`
 * attribute on the element itself, which is why designed email is inlined.
 *
 * So the rules are resolved here, at ingest, in the browser — where a real
 * selector engine already exists, and where `sanitizeForEmail` runs immediately
 * afterwards. That ordering is deliberate: this function *synthesises* style
 * attributes, and a check that ran before it would not have seen what it wrote.
 *
 * The `<style>` block is kept as well as inlined. A media query and a `:hover`
 * cannot be written onto an element, so the block still has work to do — and
 * the two together are what the rest of the pipeline expects.
 *
 * Values are moved as written. Reading `element.style` would hand back a parsed
 * CSSOM, which normalises `#1A1A1A` into `rgb(26, 26, 26)` and drops every
 * `mso-*` declaration on the floor — the exact loss this whole path exists to
 * avoid — so the style attribute is assembled as a string.
 */
import { parseStyleString } from "../html-parse";
import postcss, { Rule } from "postcss";

/** A selector we can resolve to elements, and write onto them. */
function isInlinable(selector: string): boolean {
  // A pseudo-class or pseudo-element describes a state or a box that does not
  // exist yet; there is no element to write it onto. Those stay in the block.
  if (selector.includes(":")) return false;
  return selector.trim().length > 0;
}

/**
 * How hard a selector argues for its declarations.
 *
 * The usual three-part count, flattened to one number so rules can be sorted.
 * Ties fall back to document order, which is what the cascade does.
 */
function specificity(selector: string): number {
  const ids = (selector.match(/#[\w-]+/g) ?? []).length;
  const classes = (selector.match(/\.[\w-]+|\[[^\]]*\]/g) ?? []).length;
  const elements = (
    selector.replace(/[#.][\w-]+|\[[^\]]*\]/g, " ").match(/[a-z][\w-]*/gi) ?? []
  ).length;
  return ids * 10000 + classes * 100 + elements;
}

interface Declaration {
  property: string;
  value: string;
}

interface CandidateRule {
  selector: string;
  weight: number;
  order: number;
  declarations: Declaration[];
}

function collectRules(css: string, from: number): CandidateRule[] {
  const rules: CandidateRule[] = [];
  let root;
  try {
    root = postcss.parse(css);
  } catch {
    // A stylesheet we cannot parse is one we leave entirely to the client.
    return rules;
  }

  root.walkRules((rule: Rule) => {
    // Rules nested in an at-rule are conditional — a media query, a `@supports`
    // — and inlining them would apply them unconditionally.
    if (rule.parent?.type !== "root") return;

    const declarations: Declaration[] = [];
    rule.walkDecls((decl) => {
      declarations.push({
        property: decl.prop,
        value: decl.important ? `${decl.value} !important` : decl.value,
      });
    });
    if (declarations.length === 0) return;

    for (const selector of rule.selectors) {
      if (!isInlinable(selector)) continue;
      rules.push({
        selector,
        weight: specificity(selector),
        order: from + rules.length,
        declarations,
      });
    }
  });

  return rules;
}

function serialize(declarations: Map<string, string>): string {
  return Array.from(
    declarations,
    ([property, value]) => `${property}: ${value}`,
  ).join("; ");
}

/**
 * Resolve every stylesheet in `root` onto the elements it selects.
 *
 * Mutates in place. An element's own `style` attribute is appended last and so
 * wins, which is what the cascade says: an author's inline declaration outranks
 * one from a stylesheet. A declaration the element already states inline is not
 * written twice.
 */
export function inlineStylesheets(root: ParentNode): void {
  const sheets = Array.from(root.querySelectorAll("style"));
  if (sheets.length === 0) return;

  const rules: CandidateRule[] = [];
  for (const sheet of sheets) {
    rules.push(...collectRules(sheet.textContent ?? "", rules.length));
  }
  if (rules.length === 0) return;

  // Weakest first, so a stronger rule overwrites what a weaker one wrote.
  rules.sort((a, b) => a.weight - b.weight || a.order - b.order);

  const resolved = new Map<Element, Map<string, string>>();
  for (const rule of rules) {
    let matches: Element[];
    try {
      matches = Array.from(root.querySelectorAll(rule.selector));
    } catch {
      // A selector the engine will not accept is one we cannot resolve.
      continue;
    }
    for (const element of matches) {
      let declarations = resolved.get(element);
      if (!declarations) {
        declarations = new Map<string, string>();
        resolved.set(element, declarations);
      }
      for (const { property, value } of rule.declarations) {
        declarations.set(property, value);
      }
    }
  }

  for (const [element, declarations] of Array.from(resolved)) {
    const own = element.getAttribute("style") ?? "";
    for (const property of Object.keys(parseStyleString(own))) {
      declarations.delete(property);
    }
    if (declarations.size === 0) continue;

    const merged = own.trim()
      ? `${serialize(declarations)}; ${own.trim().replace(/;$/, "")}`
      : serialize(declarations);
    element.setAttribute("style", merged);
  }
}
